import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Link, Navigate, useLocation, useNavigate, useParams, useSearchParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {LayoutSidebar} from '../../Layout';
import {Spinner} from '../Spinner';
import {Plus} from '../icons';
import {useAlertRulesAccess, useUncontrolledSpendAccess} from '../../context/UserModulesContext';
import {LocalStorage} from '../../utils/LocalStorage';
import {isOk} from '../../utils/apiResponse';
import {anchorAttr} from '../../guides/anchors';
import {AlertRulesApi, WebhookApi} from '../../api/alertRulesApi';
import {
    AUDIENCE_ADMIN,
    AUDIENCE_MEMBER,
    audienceOf,
    ERROR_ALREADY_EXISTS,
    errorFromResponse,
    insertAlertIn,
    insertRuleIn,
    isConverted,
    isSystemRule,
    KIND_SYSTEM,
    partition,
    PAUSED_LIMIT,
    removeRuleFrom,
    replaceRuleIn,
    ruleFromResponse,
    withoutSuggestion,
} from './rulesModel';
import {RulesList} from './rules/RulesList';
import {AccountNoticesView} from './rules/AccountNoticesView';
import {CreateAlertView} from './rules/CreateAlertView';
import {DetailTop} from './DetailTop';
import {WEBHOOK_ERROR, WEBHOOK_LOADING, WEBHOOK_NO_MODULE, WEBHOOK_OK} from './rules/ChannelsCard';
import {showRuleUndoToast} from './rules/ruleUndoToast';
import {MODE_EDIT, MODE_NEW, RuleBuilder} from './builder/RuleBuilder';
import {AlertsNowView} from './now/AlertsNowView';
import {UncontrolledSpendView} from './uncontrolled/UncontrolledSpendView';
import {UncontrolledSoloPage} from './uncontrolled/UncontrolledSoloPage';
import {
    ALERTS_NEW,
    ALERTS_ROOT,
    ALERTS_RULES,
    ALERTS_UNCONTROLLED,
    duplicateRulePath,
    highlightRulePath,
    NOW_VIEW_AVAILABLE,
    PARAM_DUPLICATE,
    PARAM_PAUSE,
    PARAM_SCRATCH,
    rulePath,
    VIEW_ACCOUNTS,
    VIEW_EDIT,
    VIEW_NEW,
    VIEW_NOW,
    VIEW_RULES,
    VIEW_UNCONTROLLED,
} from './alertsCenterPaths';

const NO_MODULE_ACCESS = 'NO_MODULE_ACCESS';

function webhookFromPayload(data) {
    return {
        status: WEBHOOK_OK,
        url: data.webhook_url || '',
        events: Array.isArray(data.events) ? data.events : [],
        selected: Array.isArray(data.events_selected) ? data.events_selected : [],
        // Aditivo del backend (F0): sin él, la tarjeta no afirma nada de los envíos.
        deliveries: data.deliveries || null,
    };
}

/**
 * La pantalla «Alertas» de la 2.1 (Centro de Alertas, tanda 2026-10-06): «Ahora» (F3, la bandeja de
 * todos los clientes), «Mis reglas» e «Inversión sin control» (la plata que ninguna línea mira; desde UD-5
 * también sin el Centro, sola), en ese orden. Desde UD-18 (24/10) hay además tres páginas de detalle, sin el título ni las
 * solapas, con «← Mis reglas» y la miga: «Crear una alerta» (las sugeridas y el constructor), editar una regla y
 * «Avisos de las cuentas». La solapa «Crear una alerta» se fue: queda el botón naranja de arriba.
 *
 * Una sola pantalla para todas las rutas (`/alerts`, `/alerts/rules`, `/alerts/new`, `/alerts/rules/<id>`,
 * `/alerts/rules/cuentas`): `routes.jsx` monta el MISMO elemento con otra `view`, y como React Router no
 * les pone `key` a las rutas hoja, pasar de una a otra no remonta la pantalla ni vuelve a pedir la lista.
 */
export function AlertsCenterPage({view}) {
    return (
        <div className="app-content">
            <LayoutSidebar/>
            <AlertsCenterGuard view={view}/>
        </div>
    );
}

/**
 * Sin la 2.1 o sin el flag de la agencia la pantalla no existe: vuelve al inicio. Hasta que llegan los
 * módulos no se decide nada (redirigir con el flag todavía en false rebotaría a quien sí lo tiene).
 *
 * La excepción es «Inversión sin control» (UD-5, 17/10): tiene permiso propio (toda la 2.1), así que sin el
 * Centro esa solapa se ve sola (`UncontrolledSoloPage`, sin montar el Centro). Con el Centro, todo igual; en
 * el clásico, vuelve al inicio como siempre (decisión de fede).
 */
export function AlertsCenterGuard({view}) {
    const {enabled, loaded} = useAlertRulesAccess();
    const uncontrolled = useUncontrolledSpendAccess();
    const location = useLocation();
    if (!loaded) {
        return (
            <section className="section-content alc-screen">
                <div className="box-loader"><Spinner color="positive"/></div>
            </section>
        );
    }
    if (!enabled) {
        if (view === VIEW_UNCONTROLLED && uncontrolled.enabled) return <UncontrolledSoloPage/>;
        return <Navigate to="/" replace/>;
    }
    if (view === VIEW_NOW && !NOW_VIEW_AVAILABLE) return <Navigate to={`${ALERTS_RULES}${location.search}`} replace/>;
    return <AlertsCenterContent view={view}/>;
}

function AlertsCenterContent({view}) {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const {ruleId} = useParams();
    const [searchParams] = useSearchParams();
    const userId = LocalStorage.fetchUserId() || null;
    const mounted = useRef(true);

    useEffect(() => {
        mounted.current = true;
        return () => { mounted.current = false; };
    }, []);

    // ----------------------------------------------------------- la lista (sistema + propias) ----
    const [payload, setPayload] = useState(null);
    const [listLoading, setListLoading] = useState(true);
    const [listError, setListError] = useState(null);
    // Cada pedido lleva un número: sólo escribe el último, y un cambio local (pausar, borrar) descarta
    // una recarga que salió antes, que si no lo pisaría con la foto vieja.
    const listSeq = useRef(0);
    // Ronda 4 (README v2 §4.7): quién mira (`result.viewer` de la lista sin «Ver como»: admin o no) y, para el admin,
    // la persona de «Ver como» (`viewAs`), con la que se pide la lista. En una ref también, para las recargas.
    const [me, setMe] = useState(null);
    const [viewAs, setViewAs] = useState(null);
    const viewAsRef = useRef(null);
    // Mientras llega la lista de la persona elegida, la de antes no se muestra ni se toca (review P-av2-fe-centro-02):
    // sus filas son de otra persona. Y un cambio local no descarta ese pedido (`changePayload`).
    const [viewAsPending, setViewAsPending] = useState(false);
    const viewAsPendingRef = useRef(false);
    const settleViewAs = () => {
        viewAsPendingRef.current = false;
        setViewAsPending(false);
    };

    const loadList = useCallback(({silent = false} = {}) => {
        listSeq.current += 1;
        const seq = listSeq.current;
        const asUser = viewAsRef.current ? viewAsRef.current.id : null;
        if (!silent) {
            setListLoading(true);
            setListError(null);
        }
        const request = asUser !== null ? AlertRulesApi.list({asUser}) : AlertRulesApi.list();
        request.then((response) => {
            if (!mounted.current || seq !== listSeq.current) return;
            setListLoading(false);
            if (!isOk(response) || !response.result) {
                // «Ver como» que no se pudo cargar: se vuelve a la agencia y se dice por qué. Sólo un 404 es «ya no
                // está en el equipo» (review P-av2-fe-centro-03).
                if (asUser !== null) {
                    const code = response && (response.error_code || response.status);
                    viewAsRef.current = null;
                    setViewAs(null);
                    settleViewAs();
                    toast.error(t(code === 'NOT_FOUND' ? 'alerts_center.v2.view_as_error' : 'alerts_center.v2.view_as_load_error'));
                    loadList({silent: true});
                    return;
                }
                // Una recarga de fondo que falla no tapa la lista que ya se ve.
                settleViewAs();
                if (!silent) setListError(t('alerts_center.load_error'));
                return;
            }
            if (asUser === null) setMe(response.result.viewer || null);
            setPayload(response.result);
            setListError(null);
            settleViewAs();
        });
        // `t` cambia de identidad entre renders de i18next.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const changeViewAs = useCallback((person) => {
        viewAsRef.current = person;
        setViewAs(person);
        viewAsPendingRef.current = true;
        setViewAsPending(true);
        loadList({silent: true});
    }, [loadList]);

    // Con «Ver como» puesto, lo que vuelve de una escritura viene visto como el admin: la lista se pide de nuevo como
    // esa persona (review P-av2-fe-centro-02).
    const reloadIfViewingAs = useCallback(() => {
        if (viewAsRef.current) loadList({silent: true});
    }, [loadList]);

    useEffect(() => { loadList(); }, [loadList]);

    const changePayload = useCallback((update) => {
        if (!viewAsPendingRef.current) listSeq.current += 1;
        setPayload((current) => update(current));
    }, []);

    // ------------------------------------------------- opciones del constructor (lazy) -----------
    const [options, setOptions] = useState(null);
    const [optionsState, setOptionsState] = useState('idle');

    const loadOptions = useCallback(() => {
        setOptionsState('loading');
        AlertRulesApi.options().then((response) => {
            if (!mounted.current) return;
            if (isOk(response) && response.result) {
                setOptions(response.result);
                setOptionsState('ok');
            } else {
                setOptionsState('error');
            }
        });
    }, []);

    // El constructor las necesita; el admin, además, en «Mis reglas» y «Avisos de las cuentas»: las personas del equipo
    // (el filtro «Le llegan a», el selector de «Le llega a») y los clientes del filtro «Cliente» (UD-18). Un no admin no
    // las pide: su filtro «Cliente» sale de lo que nombran sus alertas.
    const audience = audienceOf(me);
    const needsOptions = view === VIEW_NEW || view === VIEW_EDIT
        || ((view === VIEW_RULES || view === VIEW_ACCOUNTS) && audience === AUDIENCE_ADMIN && isConverted(payload));
    useEffect(() => {
        if (needsOptions && optionsState === 'idle') loadOptions();
    }, [needsOptions, optionsState, loadOptions]);

    // ------------------------------------------------- el correo de quien mira -------------------
    const [userEmail, setUserEmail] = useState(null);
    useEffect(() => {
        if (!userId) return;
        AlertRulesApi.userEmailAlerts(userId).then((response) => {
            // Si falla, los textos del correo quedan genéricos: no se inventa un «te llegan 0».
            if (mounted.current && isOk(response) && response.data) setUserEmail(response.data);
        });
    }, [userId]);

    // ------------------------------------------------- el webhook (lazy, en «Mis reglas») --------
    const [webhook, setWebhook] = useState(null);

    const loadWebhook = useCallback(() => {
        setWebhook({status: WEBHOOK_LOADING});
        WebhookApi.get().then((response) => {
            if (!mounted.current) return;
            if (isOk(response) && response.data) {
                setWebhook(webhookFromPayload(response.data));
                return;
            }
            const code = response && (response.error_code || response.status);
            if (code === NO_MODULE_ACCESS) {
                // Sin el módulo la tarjeta se ve igual, deshabilitada: con los eventos que tendría.
                WebhookApi.events().then((events) => {
                    if (!mounted.current) return;
                    setWebhook({
                        status: WEBHOOK_NO_MODULE, url: '', selected: [], deliveries: null,
                        events: isOk(events) && Array.isArray(events.data) ? events.data : [],
                    });
                });
                return;
            }
            setWebhook({status: WEBHOOK_ERROR});
        });
    }, []);

    useEffect(() => {
        if ((view === VIEW_RULES || view === VIEW_ACCOUNTS) && webhook === null) loadWebhook();
    }, [view, webhook, loadWebhook]);

    // ------------------------------------------------- lo que vuelve del constructor -------------
    const restore = useCallback((rule) => {
        AlertRulesApi.restore(rule.id).then((response) => {
            if (!isOk(response)) {
                toast.error(t('alerts_center.rules.restore_error'));
                return;
            }
            const restored = (response.result && response.result.rule) || {...rule, deleted_on: null};
            // Una de Dashbo (una excepción que volvió al número de la agencia) vuelve a su lugar, no arriba de todo.
            changePayload((current) => (isSystemRule(restored) ? insertAlertIn(current, restored) : insertRuleIn(current, restored)));
            reloadIfViewingAs();
            if (mounted.current) navigate(highlightRulePath(restored.id));
            // Con la agencia en el tope, vuelve en pausa: se dice (C-ac-fe-centro-07).
            if (restored.paused_reason === PAUSED_LIMIT) toast.warning(t('alerts_center.rules.restored_paused_limit'));
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [changePayload, navigate, reloadIfViewingAs]);

    const handleSaved = (rule, {created}) => {
        changePayload((current) => (created ? insertRuleIn(current, rule) : replaceRuleIn(current, rule)));
        loadList({silent: true});
        navigate(highlightRulePath(rule.id));
    };

    // `returned`: una excepción que volvió al número de la agencia («Volver al número de la agencia» del editor).
    const handleDeleted = (rule, {returned = false} = {}) => {
        changePayload((current) => removeRuleFrom(current, rule.id));
        navigate(ALERTS_RULES);
        showRuleUndoToast({
            message: t(returned ? 'alerts_center.row.returned_toast' : 'alerts_center.rules.deleted_toast'),
            undoLabel: t('alerts_center.rules.undo'),
            onUndo: () => restore(rule),
            actionId: rule.id,
        });
    };

    // La regla que se editaba ya no existe (otra pestaña la borró): sale de la lista y se vuelve a ella.
    const handleGone = (id) => {
        changePayload((current) => removeRuleFrom(current, id));
        navigate(ALERTS_RULES);
    };

    // Una de Dashbo convertida (v2) no se edita en el constructor: su número va en la fila. La dirección
    // `/alerts/rules/<id>` (y su `?pausar=1`, el link de un correo) lleva a su fila, resaltada.
    const handleSystemRule = (rule, {pause = false} = {}) => {
        const target = highlightRulePath(rule.id);
        navigate(pause ? `${target}&${PARAM_PAUSE}=1` : target, {replace: true});
    };

    // ------------------------------------------------- sugeridas («Crear una alerta», UD-18) -------
    // «Agregar» crea la fila al instante (`from-suggestion`) y abre su editor para revisar el número y a quién le llega;
    // al volver a la lista, la fila dice «Recién agregada» (`freshId`). P-3 de la tanda: abrir el constructor lleno
    // ANTES de crear pide backend.
    const [addingKey, setAddingKey] = useState(null);
    const [freshId, setFreshId] = useState(null);
    const addSuggestion = (suggestion) => {
        if (addingKey !== null) return;
        setAddingKey(suggestion.system_key);
        AlertRulesApi.fromSuggestion(suggestion.system_key).then((response) => {
            if (!mounted.current) return;
            setAddingKey(null);
            const created = isOk(response) ? ruleFromResponse(response) : null;
            if (!created) {
                const error = errorFromResponse(response, {fallbackKey: 'alerts_center.suggestions.add_error'});
                // Otra pestaña ya la agregó: la lista se trae de nuevo y la sugerida desaparece.
                if (error.code === ERROR_ALREADY_EXISTS) {
                    toast(t(error.key));
                    loadList({silent: true});
                    return;
                }
                toast.error(t(error.key));
                return;
            }
            const rule = {...created, kind: created.kind || KIND_SYSTEM};
            changePayload((current) => withoutSuggestion(insertAlertIn(current, rule), suggestion.system_key));
            reloadIfViewingAs();
            setFreshId(String(rule.id));
            toast.success(t('alerts_center.mr.suggestion_added'));
            navigate(rulePath(rule.id));
        });
    };

    // ------------------------------------------------------------------ dibujo -------------------
    const limits = payload ? payload.limits : null;
    // ¿Quien mira puede editar las alertas de la agencia? Lo dice el backend (`can_edit`, en la lista o
    // en `options.me`, F0 C-25). Sin el campo (backend viejo) se asume que sí: el 403 tipado igual tiene
    // su texto propio (C-ac-fe-centro-05).
    const canEdit = !(payload && payload.can_edit === false)
        && !(options && options.me && options.me.can_edit === false);

    // «Crear una alerta» (UD-18): primero las sugeridas, salvo que se pida armar una desde cero o duplicar, o que no
    // haya sugeridas para quien mira (un no admin no las agrega, D13). Mientras llega la lista no se decide.
    const scratch = searchParams.get(PARAM_SCRATCH) === '1';
    const duplicating = searchParams.get(PARAM_DUPLICATE) !== null;
    const suggestions = isConverted(payload) ? partition(payload).suggestions : [];
    const pickerAvailable = canEdit && audience !== AUDIENCE_MEMBER && suggestions.length > 0;
    const newChoice = view === VIEW_NEW && !scratch && !duplicating;
    const showPicker = newChoice && pickerAvailable;
    const waitingPicker = newChoice && !payload && listLoading;
    const team = options && Array.isArray(options.users) ? options.users : null;

    const renderBody = () => {
        // «Ahora» pide lo suyo; de Mis reglas sólo toma los nombres (la regla de `?regla=` puede no
        // tener alertas hoy y el chip igual la nombra).
        if (view === VIEW_NOW) return <AlertsNowView rules={payload ? payload.rules : []}/>;
        // No usa la lista de reglas: pide su informe (`agency/uncontrolled-spend/`).
        if (view === VIEW_UNCONTROLLED) return <UncontrolledSpendView/>;
        if (view === VIEW_ACCOUNTS) {
            return (
                <AccountNoticesView payload={payload} loading={listLoading} loadError={listError} onRetry={() => loadList()}
                                    onReload={() => loadList({silent: true})} onPayloadChange={changePayload}
                                    canEdit={canEdit} me={me} team={team} options={options} userId={userId}
                                    webhook={webhook} onWebhookSaved={setWebhook} onWebhookRetry={loadWebhook}
                                    teamState={optionsState} onRetryTeam={loadOptions}/>
            );
        }
        if (waitingPicker) return <div className="box-loader"><Spinner color="positive"/></div>;
        if (showPicker) {
            return <CreateAlertView suggestions={suggestions} canEdit={canEdit} addingKey={addingKey} onAdd={addSuggestion}/>;
        }
        if (view === VIEW_NEW || view === VIEW_EDIT) {
            // «Cancelar» (y Escape) vuelve a donde se eligió: armar una desde cero, a las sugeridas; lo demás, a la lista.
            const backTo = view === VIEW_NEW && scratch && pickerAvailable ? ALERTS_NEW : ALERTS_RULES;
            return (
                <RuleBuilder key={view === VIEW_EDIT ? `edit-${ruleId}` : 'new'}
                             mode={view === VIEW_EDIT ? MODE_EDIT : MODE_NEW} ruleId={ruleId}
                             options={options} optionsState={optionsState} onRetryOptions={loadOptions}
                             limits={limits} canEdit={canEdit} onSaved={handleSaved} onDeleted={handleDeleted}
                             onDuplicate={(rule) => navigate(duplicateRulePath(rule.id))}
                             onRuleChanged={(rule) => {
                                 changePayload((current) => replaceRuleIn(current, rule));
                                 reloadIfViewingAs();
                             }}
                             onGone={handleGone} onSystemRule={handleSystemRule}
                             onCancel={() => navigate(backTo)} viewerId={me && me.id !== undefined ? me.id : userId}
                             onlyMe={audience === AUDIENCE_MEMBER}
                             emailAlways={isConverted(payload)} canExclude={audience === AUDIENCE_ADMIN}/>
            );
        }
        return (
            <RulesList payload={payload} loading={listLoading} loadError={listError} onRetry={() => loadList()}
                       onReload={() => loadList({silent: true})}
                       onPayloadChange={changePayload} userId={userId} userEmail={userEmail} canEdit={canEdit}
                       webhook={webhook} onWebhookSaved={setWebhook} onWebhookRetry={loadWebhook}
                       me={me} viewAs={viewAs} onViewAs={changeViewAs} viewAsPending={viewAsPending}
                       team={team} options={options} freshId={freshId}
                       teamError={optionsState === 'error'} onRetryTeam={loadOptions}/>
        );
    };

    const tabClass = (active) => `uiseg__btn${active ? ' -active' : ''}`;
    const rulesTabActive = view === VIEW_RULES;
    // Las páginas de detalle (UD-18) van sin el título ni las solapas: arriba, «← Mis reglas» y la miga. El constructor
    // dibuja la suya (su «← Mis reglas» pregunta antes de descartar cambios).
    const detail = view === VIEW_EDIT || view === VIEW_NEW || view === VIEW_ACCOUNTS;

    return (
        <section className="section-content alc-screen" data-testid="alerts-center">
            <div className={`alc-page${detail ? ' -detail' : ''}`}>
                {!detail && (
                    <header className="alc-head">
                        <div className="alc-head__text">
                            <h1 className="alc-head__title">{t('alerts_center.title')}</h1>
                            <p className="alc-head__subtitle">{t('alerts_center.subtitle')}</p>
                        </div>
                        {/* «Crear una alerta» (la solapa se fue con UD-18): el ancla de las guías vino con él, así que va en
                            las tres vistas de la lista, como iba la solapa. La guía de primera visita la usa en su último paso
                            y puede arrancar en «Inversión sin control», que abre el correo diario (review C-ud18-fe-07).
                            Espera a la lista, que trae el permiso (`can_edit`): antes, a un Regular sin permiso se le
                            mostraba mientras cargaba (y siempre, con un backend que no lo mandaba). */}
                        {canEdit && payload && (
                            <Link to={ALERTS_NEW} className="alc-btn -primary" data-testid="alc-cta-new"
                                  {...anchorAttr('alerts_tab_new')}>
                                <Plus size={14} weight="bold"/>
                                {t('alerts_center.cta_new')}
                            </Link>
                        )}
                    </header>
                )}

                {/* Las solapas están en las tres pantallas de la lista: la guía de primera visita las usa para
                    no depender de en cuál entró el usuario (el correo de «Inversión sin control» abre esa solapa;
                    «Configuración de alertas», «Mis reglas»). */}
                {!detail && <nav className="uiseg alc-tabs" aria-label={t('alerts_center.title')}>
                    {NOW_VIEW_AVAILABLE && (
                        <Link to={ALERTS_ROOT} className={tabClass(view === VIEW_NOW)}
                              aria-current={view === VIEW_NOW ? 'page' : undefined}>
                            {t('alerts_center.tabs.now')}
                        </Link>
                    )}
                    {/* Orden de fede (25/10): «Mis reglas» al lado de «Ahora», «Inversión sin control» al final. */}
                    <Link to={ALERTS_RULES} className={tabClass(rulesTabActive)}
                          aria-current={rulesTabActive ? 'page' : undefined}
                          {...anchorAttr('alerts_tab_rules')}>
                        {t('alerts_center.tabs.rules')}
                    </Link>
                    <Link to={ALERTS_UNCONTROLLED} className={tabClass(view === VIEW_UNCONTROLLED)}
                          aria-current={view === VIEW_UNCONTROLLED ? 'page' : undefined}
                          {...anchorAttr('alerts_tab_uncontrolled')}>
                        {t('alerts_center.tabs.uncontrolled')}
                    </Link>
                </nav>}

                {view === VIEW_ACCOUNTS && (
                    <DetailTop crumbs={[t('alerts_center.title'), t('alerts_center.tabs.rules'), t('alerts_center.accounts.title')]}/>
                )}
                {(showPicker || waitingPicker) && <DetailTop crumbs={[t('alerts_center.title'), t('alerts_center.cta_new')]}/>}

                {renderBody()}
            </div>
        </section>
    );
}

export default AlertsCenterPage;
